import {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'
import Fuse from 'fuse.js'
import {
  CommandPaletteFilter,
  CommandPaletteGroup,
  CommandPaletteItem,
} from './types'
import { CommandPaletteInput } from './CommandPaletteInput'
import { CommandPaletteFilters } from './CommandPaletteFilters'
import { CommandPaletteResults } from './CommandPaletteResults'
import { CommandPaletteInstructions } from './CommandPaletteInstructions'
import styles from './CommandPalette.module.css'

interface CommandPaletteProps<T = unknown> {
  groups: CommandPaletteGroup[]
  filters?: CommandPaletteFilter[]
  fuseIndex?: Fuse<CommandPaletteItem<T>>
  onSelect: (item: CommandPaletteItem<T>) => void
  onQueryChange?: (query: string) => void
  placeholder?: string
  shortcut?: ReactNode
  status?: ReactNode
}

export function CommandPalette<T = unknown>({
  groups,
  filters,
  fuseIndex,
  onSelect,
  onQueryChange,
  placeholder,
  shortcut,
  status,
}: CommandPaletteProps<T>) {
  const [query, setQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState<string>(
    filters?.[0]?.id ?? 'all'
  )
  const [activeIndex, setActiveIndex] = useState(0)

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value)
      setActiveIndex(0)
      onQueryChange?.(value)
    },
    [onQueryChange]
  )

  const handleFilterChange = useCallback((filterId: string) => {
    setActiveFilter(filterId)
    setActiveIndex(0)
  }, [])

  const visibleGroups = useMemo((): CommandPaletteGroup[] => {
    if (fuseIndex && query) {
      const results = fuseIndex.search(query, { limit: 50 })
      const flatItems = results.map((r) => r.item)

      const groupLabelMap = new Map(
        groups.map((g) => [g.id, g.label])
      )
      const groupOrder = groups.map((g) => g.id)

      const grouped = new Map<string, CommandPaletteItem[]>()
      for (const item of flatItems) {
        const existing = grouped.get(item.group) ?? []
        existing.push(item)
        grouped.set(item.group, existing)
      }

      return groupOrder
        .filter((gid) => grouped.has(gid))
        .map((gid) => ({
          id: gid,
          label: groupLabelMap.get(gid) ?? gid,
          items: grouped.get(gid)!,
        }))
    }

    if (activeFilter && activeFilter !== 'all') {
      return groups.filter((g) => g.id === activeFilter)
    }

    return groups.map((g) => ({ ...g }))
  }, [fuseIndex, query, groups, activeFilter])

  const flatItems = useMemo(
    () => visibleGroups.flatMap((g) => g.items),
    [visibleGroups]
  )

  const handleItemActivate = useCallback((flatIdx: number) => {
    setActiveIndex(flatIdx)
  }, [])

  const handleItemSelect = useCallback(
    (item: CommandPaletteItem) => {
      onSelect(item as CommandPaletteItem<T>)
    },
    [onSelect]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowUp':
        case 'p': {
          if (e.key === 'p' && !e.ctrlKey) break
          e.preventDefault()
          setActiveIndex((i) =>
            i <= 0 ? flatItems.length - 1 : i - 1
          )
          break
        }
        case 'ArrowDown':
        case 'n': {
          if (e.key === 'n' && !e.ctrlKey) break
          e.preventDefault()
          setActiveIndex((i) =>
            i >= flatItems.length - 1 ? 0 : i + 1
          )
          break
        }
        case 'Enter': {
          e.preventDefault()
          const item = flatItems[activeIndex]
          if (item) {
            onSelect(item as CommandPaletteItem<T>)
          }
          break
        }
        case 'Escape': {
          e.preventDefault()
          handleQueryChange('')
          break
        }
      }
    },
    [flatItems, activeIndex, onSelect, handleQueryChange]
  )

  return (
    <div
      className={styles.CommandPalette}
      role="dialog"
      aria-label="Command palette"
    >
      <CommandPaletteInput
        value={query}
        onChange={handleQueryChange}
        placeholder={placeholder}
        shortcut={shortcut}
        autoFocus
        onKeyDown={handleKeyDown}
      />
      {filters && filters.length > 0 && (
        <CommandPaletteFilters
          filters={filters}
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
        />
      )}
      <CommandPaletteResults
        groups={visibleGroups}
        activeIndex={activeIndex}
        onItemActivate={handleItemActivate}
        onItemSelect={handleItemSelect}
        query={query || undefined}
      />
      <CommandPaletteInstructions status={status} />
    </div>
  )
}
